/**
 * テキスト層（EP-4）の取得（誌面の代替テキスト用・FR-A11Y / WCAG 1.1.1）。
 *
 * 索引と同じ唯一のテキスト源（`GET /api/v1/books/{id}/pages/{p}/text`）を読み、
 * ページ本文の文字列へ戻す。取れないページ（テキスト層の無い本・EP-5 の許可外ページ・
 * 通信失敗）は null を返し、呼び出し側は「ページ N / 全 M」へ縮退する。
 *
 * 中断以外の失敗をここで飲み込むのは、代替テキストが取れないことを閲覧の失敗に
 * しないため（誌面は描けているので、読み上げだけが縮退すればよい）。
 */
import type { ManifestSession } from '../core/manifest';
import { signedFetch } from './signed-fetch';

/** テキスト層 JSON（サーバ TextStep の出力）。使うのは本文の復元に要るキーだけ。 */
interface PageTextJson {
  runs?: { t?: string; lang?: string }[];
  offsets?: number[];
}

/**
 * 本文の一区切り。`lang` は**書籍の主言語と違う句・節にだけ**付く（WCAG 3.1.2）。
 * 取り込み時に「主言語と違う文字体系の連なり」へ印が付いており、単発の外来語には付かない。
 */
export interface TextSegment {
  text: string;
  lang?: string;
}

export interface TextSource {
  /** ページ本文（取れなければ null）。中断されたときも null を返す。 */
  get(pageNo: number, signal: AbortSignal): Promise<readonly TextSegment[] | null>;
}

/**
 * ラン列をページ本文へ戻す。
 *
 * 語と語のあいだに区切りを入れるかはサーバ（Ingest\TextBuilder）が決めており、
 * その結果は `offsets`（各ランの開始コードポイント位置）に現れる。規則を写し取らず
 * offsets の隙間だけを見るのは、連結規則の定義点を 1 つに保つため。
 */
export function joinRuns(json: PageTextJson): readonly TextSegment[] {
  const runs = json.runs ?? [];
  const offsets = json.offsets ?? [];
  const segments: TextSegment[] = [];
  let length = 0; // ここまでの長さ（offsets と同じくコードポイント数で数える）

  runs.forEach((run, index) => {
    const body = run.t ?? '';
    // 隙間があれば区切り 1 個。壊れた offsets で長い空白を作らないよう 1 個に丸める。
    const gap = (offsets[index] ?? length) > length;
    if (gap) length += 1;

    // 同じ言語が続くあいだは 1 区切りに束ねる（span を語ごとに刻まない）。
    const last = segments[segments.length - 1];
    if (last !== undefined && last.lang === run.lang) {
      last.text += (gap ? ' ' : '') + body;
    } else {
      segments.push(run.lang === undefined ? { text: body } : { text: body, lang: run.lang });
      // 区切りは前の区切れの末尾へ付ける（言語が変わる位置に空白を残さない）
      if (gap && segments.length > 1) {
        const previous = segments[segments.length - 2] as TextSegment;
        previous.text += ' ';
      }
    }
    length += [...body].length;
  });
  return segments;
}

/** 区切りを 1 本の文字列へ戻す（言語の別が要らない場面用）。 */
export function segmentsToText(segments: readonly TextSegment[]): string {
  return segments.map((segment) => segment.text).join('');
}

/**
 * テキスト層の読み手。テキスト層を持たない本（マニフェストに `urls.text` が無い）では
 * null を返す＝呼び出し側は縮退表示だけを出す。
 */
export function createTextSource(session: ManifestSession): TextSource | null {
  if (session.current.urls.text === undefined) return null;

  // ページ本文は小さな文字列なので、めくり戻しのたびに取り直さず持っておく
  // （ラスタと違い、抱えても表示資源を占有しない）。
  const cache = new Map<number, readonly TextSegment[]>();

  return {
    async get(pageNo: number, signal: AbortSignal): Promise<readonly TextSegment[] | null> {
      const cached = cache.get(pageNo);
      if (cached !== undefined) return cached;

      const key = String(pageNo);
      // 許可外ページ（EP-5）にはマニフェストが URL を載せない。
      const known = session.current.urls.text?.[key];
      if (known === undefined) return null;

      try {
        const response = await signedFetch(session, (m) => m.urls.text?.[key] ?? known, { signal });
        if (!response.ok) return null;
        const segments = joinRuns((await response.json()) as PageTextJson);
        cache.set(pageNo, segments);
        return segments;
      } catch {
        return null;
      }
    },
  };
}
